import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { consentApi, type ConsentDocumentResponse } from '../api/client';
import type { ConsentItem } from '../utils/consentItems';
import {
  isDocumentNotOpened,
  isOpenApiMissing,
  versionForGrant,
  type OpenedDocument,
} from '../utils/consentOpen';

function formatDate(iso: string | null | undefined) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' });
  } catch {
    return iso;
  }
}

interface Props {
  items: ConsentItem[];
  /** 一覧で受け取った本文。本文を開く API が無い古い BE のときに代わりに使う */
  documents: ConsentDocumentResponse[];
  /** 同意した日を出すか（設定の画面で出す） */
  showDate?: boolean;
  /** 1 列目（目的）の表示名 */
  label: (item: ConsentItem) => string;
  /** 同意・撤回が済んだあと、一覧を読み込み直す */
  onChanged: () => void;
}

/**
 * 同意の項目の表（設定の「同意と利用範囲」と、初回の同意で共通。課題 #77）。
 *
 * 2026-09-29 高尾さん決定: 対応する文書の本文を開くまで「同意する」を押せないようにする。
 * 本文は「本文を読む」で本文を開く API（GET /v1/consent/documents/{id}）から取得し、
 * その場に表示する。開いた時刻はサーバーが記録し、同意に結び付ける（BE PR #130）。
 * 端末から送った時刻に頼らないため、証跡として残せる（「本当に読んだか」までは確かめられない）。
 */
export default function ConsentChoicesTable({ items, documents, showDate = false, label, onChanged }: Props) {
  const { t } = useTranslation();
  const { getToken } = useAuth();
  const [opened, setOpened] = useState<Record<string, OpenedDocument>>({});
  const [expanded, setExpanded] = useState<string | null>(null);
  const [opening, setOpening] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const forget = (id: string) =>
    setOpened((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });

  /** 本文を開く（もう一度押すと閉じる） */
  const toggleDocument = async (item: ConsentItem) => {
    const id = item.document.id;
    if (expanded === id) {
      setExpanded(null);
      return;
    }
    setExpanded(id);
    setError(null);
    if (opened[id]) return;
    const token = getToken();
    if (!token) return;
    setOpening(id);
    try {
      const doc = await consentApi.openDocument(id, token);
      setOpened((prev) => ({ ...prev, [id]: { version: doc.version, content: doc.content ?? '', legacy: false } }));
    } catch (err) {
      if (isOpenApiMissing(err)) {
        // 本文を開く API が無い古い BE。一覧の本文を使い、これまでどおり同意できるようにする
        const listed = documents.find((d) => d.id === id);
        setOpened((prev) => ({
          ...prev,
          [id]: { version: item.document.version, content: listed?.content ?? '', legacy: true },
        }));
      } else {
        setExpanded(null);
        setError(err instanceof Error ? err.message : t('common.error'));
      }
    } finally {
      setOpening(null);
    }
  };

  const handleGrant = async (item: ConsentItem) => {
    const token = getToken();
    const d = item.document;
    const doc = opened[d.id];
    if (!token || !doc) return;
    setError(null);
    setActionLoading(d.id);
    try {
      await consentApi.grant(
        {
          purpose: d.purpose,
          data_type: d.data_type,
          // 開いた版で同意する（一覧を読んだ後に改訂されていても、読んだ版と同意が食い違わない）
          version: versionForGrant(d.version, doc),
          document_id: d.id,
        },
        token
      );
      onChanged();
    } catch (err) {
      if (isDocumentNotOpened(err)) {
        // 改訂された・開いた記録が見つからない。開き直してもらう
        forget(d.id);
        setExpanded(null);
        setError(t('settings.consent.documentNotOpened'));
      } else {
        setError(err instanceof Error ? err.message : t('common.error'));
      }
    } finally {
      setActionLoading(null);
    }
  };

  const handleRevoke = async (item: ConsentItem) => {
    const token = getToken();
    const d = item.document;
    if (!token) return;
    setError(null);
    setActionLoading(d.id);
    try {
      await consentApi.revoke(
        { purpose: d.purpose, data_type: d.data_type, version: d.version, document_id: d.id },
        token
      );
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setActionLoading(null);
    }
  };

  const columns = showDate ? 6 : 5;

  return (
    <>
      <table className="settings-table consent-choices-table">
        <thead>
          <tr>
            <th scope="col">{t('settings.consent.purpose')}</th>
            <th scope="col">{t('settings.consent.dataType')}</th>
            <th scope="col">{t('settings.consent.status')}</th>
            {showDate && <th scope="col">{t('settings.consent.consentDate')}</th>}
            <th scope="col">{t('settings.consent.documentColumn')}</th>
            <th scope="col"></th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const id = item.document.id;
            const doc = opened[id];
            const isOpen = expanded === id;
            return (
              <Fragment key={id}>
                <tr data-testid="consent-row">
                  <td>{label(item)}</td>
                  <td>{item.document.data_type_label_ja || item.document.data_type}</td>
                  <td>
                    <span className={`settings-badge ${item.granted ? 'settings-badge-ok' : ''}`}>
                      {item.granted ? t('settings.consent.granted') : t('settings.consent.notGranted')}
                    </span>
                  </td>
                  {showDate && <td>{formatDate(item.record?.granted_at ?? null)}</td>}
                  <td>
                    <button
                      type="button"
                      className="settings-btn"
                      onClick={() => toggleDocument(item)}
                      aria-expanded={isOpen}
                      disabled={opening === id}
                    >
                      {opening === id
                        ? t('settings.consent.openingDocument')
                        : isOpen
                        ? t('settings.consent.closeDocument')
                        : t('settings.consent.readDocument')}
                    </button>
                  </td>
                  <td>
                    {item.granted ? (
                      <button
                        type="button"
                        className="settings-btn settings-btn-danger"
                        onClick={() => handleRevoke(item)}
                        disabled={actionLoading === id}
                      >
                        {actionLoading === id ? t('common.loading') : t('settings.consent.revoke')}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="settings-btn settings-btn-primary"
                        onClick={() => handleGrant(item)}
                        disabled={!doc || actionLoading === id}
                        title={doc ? undefined : t('settings.consent.grantNeedsOpen')}
                      >
                        {actionLoading === id ? t('common.loading') : t('settings.consent.grant')}
                      </button>
                    )}
                  </td>
                </tr>
                {isOpen && (
                  <tr className="consent-choices-document-row">
                    <td colSpan={columns}>
                      {opening === id || !doc ? (
                        <p>{t('settings.consent.openingDocument')}</p>
                      ) : doc.content ? (
                        <pre className="settings-consent-doc-content" data-testid="consent-document-body">
                          {doc.content}
                        </pre>
                      ) : (
                        <p className="settings-consent-doc-empty">{t('settings.consent.documentNoBody')}</p>
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
      {items.some((i) => !i.granted) && (
        <p className="settings-consent-doc-hint">{t('settings.consent.grantHint')}</p>
      )}
      {error && (
        <p className="settings-form-error" role="alert">
          {error}
        </p>
      )}
    </>
  );
}
